import { CheckCircleOutlined, CommentOutlined, CrownOutlined, InfoCircleOutlined, QuestionOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Col, Collapse, Form, Input, List, Modal, Pagination, Radio, Row, Skeleton, Table, Tooltip } from "antd";
import { useForm } from "antd/es/form/Form";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from 'antd/es/table';

import './AgencySelectorComponent.scss';
import { AddAgency, AgencyFilter, SmallAgencyData, SmallAgencyDataList } from "../../../models/SmallAgencyData";
import { useEffect, useState } from "react";
import HTMLReactParser from "html-react-parser";
import { AgencyAndCompanyAddForm, AgencyAndCompanyRequestSignUpForm } from "../..";
import { GetInversedWarnIcon, GetNoResultIncon } from "../../../utils/icons";
import { useAppConfig } from "../../../Contexts/AppConfigContext";
import ApiResponse from "../../../models/api/ApiResponse";
import { GetPaginationLocale } from "../../../utils/locales";
import { AddCompany } from "../../../models/SmallCompanyData";

interface AgencySelectorComponentProps {
    getAgenciesData: (filter: AgencyFilter) => Promise<SmallAgencyDataList>,
    onNextStep: (step?: number, extra?: string) => void,
    onPreviousStep: (step?: number, extra?: string) => void,
    onSelectAgency: (agencyData: SmallAgencyData) => void,
    onSignUpRequest: (type: string, selectedId: string, email: string, agentName: string, verificationCode: string) => Promise<ApiResponse | null>
}

const AgencySelectorComponent = (props: AgencySelectorComponentProps): JSX.Element => {
    const { t } = useTranslation();

    const appConfig = useAppConfig();

    const defaultFilter = {
        address: "",
        commercialName: "",
        fiscalId: "",
        phone: "",
        numRecords: 6,
        page: 1
    };

    const [showAgencyRequestForm, setShowAgencyRequestForm] = useState<boolean>(false);

    const { onNextStep, onPreviousStep, onSelectAgency, getAgenciesData, onSignUpRequest } = props;

    const [agenciesData, setAgenciesData] = useState<SmallAgencyDataList>({ agencies: [], totalRecords: 0 });

    const [isLoadingData, setIsLoading] = useState<boolean>(false);
    const [isNextButtonActive, setIsNextButtonActive] = useState<boolean>(false);

    const [form] = useForm();

    const [selectedNumRecords, setSelectedNumRecords] = useState<number>(6);
    const [selectedPage, setSelectedPage] = useState<number>(1);

    const [showRequestRegistration, setShowRequestRegistration] = useState<boolean>(false);
    let counter: number = 0;
    const getAgencies = async (filterData: AgencyFilter) => {
        counter++;
        setIsLoading(true);
        var data = await getAgenciesData(filterData);
        setShowRequestRegistration(data.totalRecords <= 6 && data.totalRecords > 0);
        setIsLoading(false);
        setAgenciesData(data);
    }

    const getFilteredData = (page?: number, records?: number): void => {
        var filterData: AgencyFilter = {
            address: form.getFieldValue("address") ?? "",
            commercialName: form.getFieldValue("commercialName") ?? "",
            fiscalId: form.getFieldValue("fiscalId") ?? "",
            phone: form.getFieldValue("phone") ?? "",
            numRecords: records ?? selectedNumRecords,
            page: page ?? selectedPage
        }

        getAgencies(filterData);
    }


    const onFormChange = (): void => {
        getFilteredData();
    }

    const onChangeAgency = (x: any): void => {
        if (x?.target?.value?.length > 0) {
            const agencyData: SmallAgencyData = JSON.parse(x.target.value);

            if (agencyData.isAlreadyUsed) {
                setIsNextButtonActive(false);
                showIsUsedForm(agencyData)
            } else {
                onSelectAgency(agencyData);
                setIsNextButtonActive(true);
            }
        }
    }

    const closeIsUsedModal = (): void => {
        setSelectedRowKeys([]);
        setShowIsUsed(false);
    }

    const closeAddRecordModal = (): void => setShowAgencyRequestForm(false);

    const onChangePageOrNumRecords = (page: number, numRecords: number) => {
        setSelectedPage(page);
        setSelectedNumRecords(numRecords);
        getFilteredData(page, numRecords);
    }




    // rowSelection object indicates the need for row selection
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedRowKeys: React.Key[], selectedRows: SmallAgencyData[]) => {
            setSelectedRowKeys(selectedRowKeys);
            if (selectedRows[0].isAlreadyUsed) {
                setIsNextButtonActive(false);
                showIsUsedForm(selectedRows[0])
            } else {
                onSelectAgency(selectedRows[0]);
                setIsNextButtonActive(true);
            }
        }
    };

    const [showIsUsed, setShowIsUsed] = useState<boolean>(false);
    const [selectedAgencyData, setSelectedAgencyData] = useState<SmallAgencyData>();
    const showIsUsedForm = (agencyData: SmallAgencyData): void => {
        setSelectedAgencyData(agencyData);
        setShowIsUsed(true);
    }

    const tableColumns: ColumnsType<SmallAgencyData> = [
        {
            title: t('components.agency-selector.filter.commercial-name'),
            dataIndex: "name",
            key: "commercialName",
            render: (item: string, record: SmallAgencyData) => {
                return <div className="agency-name">
                    {item}
                </div>
            }
        },
        {
            title: t('components.agency-selector.filter.address'),
            dataIndex: "address",
            key: "address",
            render: (item: string) => item
        },
        {
            title: t('components.agency-selector.filter.phone'),
            dataIndex: "telephone",
            key: "telephone",
            render: (item: string) => item
        },
        {
            title: t('components.agency-selector.filter.fiscal-id'),
            dataIndex: "identityCode",
            key: "identityCode",
            render: (item: string, record: SmallAgencyData) => {
                return <div className="agency-name">

                    {
                        record.isAlreadyUsed ?
                            <div className="triangle">
                                <Tooltip title={t(`components.agency-selector.this-agency-is-used`)}>
                                    <CrownOutlined style={{ color: "white", fontSize: "13px" }} className="question" />
                                </Tooltip>
                            </div>
                            :
                            <></>
                    }
                    {item}
                </div>
            }
        }
    ];

    const [isRequesting, setIsRequesting] = useState<boolean>(false);
    const signUpRequest = (email: string, name: string, verificationCode: string): void => {
        if (selectedAgencyData) {
            setIsRequesting(true);
            onSignUpRequest("agency", selectedAgencyData?.id, email, name, verificationCode).then((r: ApiResponse | null) => {
                if (r?.type === 1) {
                    setShowIsUsed(false);
                    onNextStep(3, 'waiting-request');
                }
            })
                .finally(() => {
                    setIsRequesting(false);
                })
        }

    }

    const onCreatedRecord = (organizationData: AddAgency | AddCompany): void => {
        closeAddRecordModal();
        form.setFieldValue("commercialName", organizationData.commercialName);
        form.setFieldValue("fiscalId", organizationData.fiscalId);
        getFilteredData(1, 1);
    }


    useEffect(() => {
        getFilteredData();
    }, [])

    return (
        <div className="agency-selector-component">
            <Modal className="app-modal no-header-boreder"
                title={
                    <Row>
                        <Col xs={4} sm={2}>
                            {GetInversedWarnIcon('app-icon')}
                        </Col>
                        <Col xs={20} sm={22}>
                            {
                                HTMLReactParser(t("components.agency-selector.modal-request-title", [selectedAgencyData?.name]))
                            }
                        </Col>
                    </Row>
                }
                open={showIsUsed}
                footer={null}
                onCancel={closeIsUsedModal}>
                <AgencyAndCompanyRequestSignUpForm
                    key="modal-form-request-sign-up"
                    hasVerificationCode={selectedAgencyData?.hasVerificationCode || false}
                    name={selectedAgencyData?.name!}
                    resetForm={showIsUsed}
                    onSignUpRequest={signUpRequest}
                    isRequesting={isRequesting}
                />
            </Modal>

            <Modal className="app-modal" title={t("components.agency-selector.modal-title")} open={showAgencyRequestForm} footer={null} onCancel={closeAddRecordModal}>
                {t("components.agency-selector.modal-subtitle")}
                <AgencyAndCompanyAddForm key="modal-form-add-record" recordType="agency" onCreatedRecord={onCreatedRecord} onCloseModal={closeAddRecordModal} />
            </Modal>

            <div className="filter">
                <Form form={form} layout="vertical" >
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={t("components.agency-selector.filter.commercial-name")}
                                name="commercialName"
                            >
                                <Input onChange={onFormChange} allowClear className="app-input" prefix={<SearchOutlined className="prefix-search-icon" />} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                label={t("components.agency-selector.filter.address")}
                                name="address"
                            >
                                <Input onChange={onFormChange} allowClear className="app-input" prefix={<SearchOutlined className="prefix-search-icon" />} />
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Collapse className="advanced-search-container" items={[
                                {
                                    key: '1',
                                    label: t('components.agency-selector.filter.advanced-search'),
                                    headerClass: "advanced-search-header right-align",
                                    children: <Row gutter={16}>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                label={t("components.agency-selector.filter.phone")}
                                                name="phone"
                                            >
                                                <Input onChange={onFormChange} allowClear className="app-input" prefix={<SearchOutlined className="prefix-search-icon" />} />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                label={t("components.agency-selector.filter.fiscal-id")}
                                                name="fiscalId"
                                            >
                                                <Input onChange={onFormChange} allowClear className="app-input" prefix={<SearchOutlined className="prefix-search-icon" />} />
                                            </Form.Item>
                                        </Col>
                                    </Row>,
                                }
                            ]} />
                        </Col>
                    </Row>
                </Form>
            </div>
            {
                agenciesData.totalRecords == 0 ?
                    <div className="no-records-found">
                        {GetNoResultIncon("app-icon")}
                        {t("components.agency-selector.not-record-found")}
                        <div className="request-agency-registration" onClick={() => { setShowAgencyRequestForm(true) }}>
                            {HTMLReactParser(t('components.agency-selector.modify-or-add-record'))}
                        </div>
                    </div>
                    :
                    <div className="agency-list">
                        <Skeleton active loading={isLoadingData}>

                            {
                                appConfig.device !== "mobile"
                                    ?
                                    <Table className="app-table" columns={tableColumns} dataSource={agenciesData.agencies} rowSelection={{ type: 'radio', ...rowSelection, }} pagination={false} rowKey={"id"} />
                                    :
                                    <div className="app-list-container">
                                        <div className="headers">
                                            <Row gutter={0}>
                                                <Col xs={24} >
                                                    <Row>
                                                        <Col xs={2} className="mobile-column"></Col>
                                                        <Col xs={12} className="mobile-column">Nombre comercial</Col>
                                                        <Col xs={10} className="mobile-column">Id. Fiscal</Col>
                                                    </Row>
                                                </Col>
                                            </Row>

                                        </div>
                                        <List
                                            className={`app-list`}
                                            itemLayout="vertical"
                                            dataSource={agenciesData.agencies}
                                            renderItem={(item: SmallAgencyData) =>
                                                <List.Item >
                                                    <Row gutter={0}>
                                                        <Col xs={3} className="list-item flex-center" style={{ minHeight: "60px" }}>
                                                            <input onChange={onChangeAgency} type="radio" id={item.identityCode!} name="selectedAgency" value={JSON.stringify(item)} className="ant-radio-input"></input>
                                                        </Col>
                                                        <Col xs={11} className="list-item flex-center" style={{ minHeight: "60px" }}>
                                                            <span className="agency-name">{item.name.toUpperCase()}</span>
                                                        </Col>
                                                        <Col xs={10} className="list-item flex-center fiscal-id" style={{ minHeight: "60px" }}>
                                                            {
                                                                item.isAlreadyUsed
                                                                    ?
                                                                    <div className="triangle">
                                                                        <Tooltip title={t(`components.agency-selector.this-agency-is-used`)}>
                                                                            <CrownOutlined style={{ color: "white", fontSize: "13px" }} className={`question ${appConfig.device}`} />
                                                                        </Tooltip>
                                                                    </div>
                                                                    :
                                                                    <></>
                                                            }
                                                            {item.identityCode}
                                                        </Col>

                                                    </Row>
                                                    <Row className="list-additional-info">
                                                        <Col xs={24} className="list-item">
                                                            <strong>{t('components.agency-selector.address')}: </strong>
                                                            <span>{item.address}</span>
                                                        </Col>
                                                        <Col xs={24} className="list-item">
                                                            <strong>{t('components.agency-selector.phone')}: </strong>
                                                            <span>{item.telephone}</span>
                                                        </Col>
                                                    </Row>

                                                </List.Item>
                                            }
                                        />
                                    </div>
                            }

                            <div className="total-records">
                                <div className="pagination">
                                    <Pagination
                                        locale={GetPaginationLocale(t)}
                                        total={agenciesData.totalRecords}
                                        pageSizeOptions={["6", "20", "50", "100"]}
                                        defaultPageSize={selectedNumRecords}
                                        defaultCurrent={selectedPage}
                                        onChange={onChangePageOrNumRecords}
                                    />
                                </div>
                                <div className="counter">
                                    {t('components.agency-selector.total-records', [agenciesData.totalRecords])}
                                </div>
                            </div>
                        </Skeleton>
                    </div>
            }

            {
                showRequestRegistration
                    ?
                    <div className="request-agency-registration" onClick={() => { setShowAgencyRequestForm(true) }}>
                        {HTMLReactParser(t('components.agency-selector.new-record-request'))}
                    </div>
                    :
                    <>
                    </>
            }
            <div className="actions">
                <Button onClick={() => { onPreviousStep(); }} className="app-white-button">
                    {t('components.agency-selector.actions.prev')}
                </Button>

                <Button onClick={() => { onNextStep(); }} className="app-button" disabled={!isNextButtonActive}>
                    {t('components.agency-selector.actions.next')}
                </Button>
            </div>
        </div>
    )
}


export default AgencySelectorComponent;